import { FilterExpression } from '@pushwoosh/rpc-service-client/pushwoosh/objects/filters/v1/model_pb';
import {
  type Condition,
  type ConditionTag,
  type ConditionEvent,
  type ConditionValue,
  type ConditionOperator,
  type GroupOperator,
} from '../../types';
import {
  type OperatorConfig,
  type ValueTypeConfig,
  operators,
  valuesTypes,
} from './common';
import OperationOperator = FilterExpression.Operation.Operator;

function getDaysAgoOperator(
  daysAgo: FilterExpression.Tag.Date.DaysAgo,
  OperatorEnum: any,
): string {
  const daysAgoOperatorsMap: Record<any, string> = {
    [OperatorEnum.OPERATOR_EQ]: 'eq',
    [OperatorEnum.OPERATOR_NEQ]: 'noteq',
    [OperatorEnum.OPERATOR_LTE]: 'lte',
    [OperatorEnum.OPERATOR_GTE]: 'gte',
    [OperatorEnum.OPERATOR_BETWEEN]: 'between',
  };
  return `daysago ${daysAgoOperatorsMap[daysAgo.getOperator()]}`;
}

function getConditionTagData(
  opConf: OperatorConfig,
  operatorResult: any,
  ObjClass: any,
): [string, any[]] {
  const operator = opConf.getOperator?.(operatorResult) ?? opConf.operator!;
  if (operator === 'between') {
    return [operator, [operatorResult.getFrom(), operatorResult.getTo()]];
  }
  if (operator === 'in' || operator === 'notin') {
    return [operator, operatorResult.getValuesList()];
  }
  if (operator === 'daysago') {
    const daOperator = getDaysAgoOperator(
      operatorResult,
      ObjClass.Date.DaysAgo.Operator,
    );
    return [
      daOperator,
      daOperator === 'daysago between'
        ? operatorResult.getValuesList()
        : [operatorResult.getValue()],
    ];
  }
  if (operator === 'any' || operator === 'notset') {
    return [operator, []];
  }
  return [operator, [operatorResult.getValue()]];
}

function toConditionValue(obj: any, ObjClass: any): ConditionValue {
  const tagConf = valuesTypes.find((t) =>
    (obj as any)[`has${t.method}`](),
  ) as ValueTypeConfig;
  const methodResult = (obj as any)[`get${tagConf.method}`]();
  const opConf = operators.find((op) =>
    methodResult[`has${op.method}`]?.(),
  ) as OperatorConfig;
  const operatorResult = methodResult[`get${opConf.method}`]();
  const [operator, values] = getConditionTagData(
    opConf,
    operatorResult,
    ObjClass,
  );
  return {
    type: tagConf.tagType,
    name: obj.getName(),
    operator: operator as ConditionOperator,
    values: tagConf.convertFromDb ? values.map(tagConf.convertFromDb) : values,
  };
}

function convertTag(tag: FilterExpression.Tag): ConditionTag<ConditionValue> {
  return {
    type: 'tag',
    tag: toConditionValue(tag, FilterExpression.Tag),
  };
}

const eventCountOperatorsMap: Record<any, string> = {
  [FilterExpression.Event.Count.Operator.OPERATOR_EQ]: 'eq',
  [FilterExpression.Event.Count.Operator.OPERATOR_LTE]: 'lte',
  [FilterExpression.Event.Count.Operator.OPERATOR_GTE]: 'gte',
};

function convertEventCountOperator(operator: any): ConditionOperator {
  return (eventCountOperatorsMap[operator] as ConditionOperator) ?? 'eq';
}

function convertEvent(
  event: FilterExpression.Event,
): ConditionEvent<ConditionValue> {
  return {
    type: 'event',
    event: {
      name: event.getEvent(),
      count: {
        name: 'count',
        type: 'integer',
        operator: convertEventCountOperator(event.getCount()!.getOperator()),
        values: [event.getCount()!.getValue()],
      },
      lastDays: event.getDate()!.getDaysAgo()!.getValue(),
      attributes: event.getAttributesList().map((attr) => {
        return toConditionValue(attr, FilterExpression.Event.Attribute);
      }),
    },
  };
}

function getGroupOperator(op: OperationOperator): GroupOperator {
  const daysAgoOperatorsMap: Record<any, GroupOperator> = {
    [OperationOperator.OPERATOR_INTERSECT]: 'and',
    [OperationOperator.OPERATOR_UNION]: 'or',
    [OperationOperator.OPERATOR_DIFFERENCE]: 'and not',
  };
  return daysAgoOperatorsMap[op];
}
export function parseFilterExpression(expression: FilterExpression): Condition {
  if (expression.hasOperation()) {
    const operation = expression.getOperation()!;
    const operator = getGroupOperator(operation.getOperator());
    const conditions = operation.getOperandsList().map(parseFilterExpression);
    return {
      operator,
      conditions,
    };
  }
  if (expression.hasTag()) {
    return convertTag(expression.getTag()!);
  }
  if (expression.hasEvent()) {
    return convertEvent(expression.getEvent()!);
  }
  throw new Error(`Unknown expression kind: ${expression.getKindCase()}`);
}
